import React, { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Users, Store, ShoppingBag, Edit2, Save, X, Trash2, 
  Search, RefreshCw, Loader2, Eye, ChevronDown, ChevronUp,
  Plus, Mail, Phone, Shield, Check, AlertCircle
} from "lucide-react";
import { toast } from "sonner";
import authService from "../../services/authService";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminOverview = () => {
  const [activeTab, setActiveTab] = useState("afiliados");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [expandedUser, setExpandedUser] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newUser, setNewUser] = useState({ nome: "", email: "", senha: "ClickHub2026!", tipo: "afiliado", telefone: "" });

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await authService.fetchAuth(`${BACKEND_URL}/api/admin/overview/users?tipo=${activeTab === "afiliados" ? "afiliado" : "fornecedor"}`);
      const data = await res.json();
      setUsers(data.users || []);
    } catch (e) {
      console.error("Erro:", e);
      toast.error("Erro ao carregar usuarios");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, [activeTab]);

  const startEdit = (user) => {
    setEditingUser(user.id);
    setEditForm({ nome: user.nome, email: user.email, telefone: user.telefone || "", ativo: user.ativo });
  };

  const cancelEdit = () => { setEditingUser(null); setEditForm({}); };

  const saveEdit = async (userId) => {
    try {
      setSaving(true);
      const res = await authService.fetchAuth(`${BACKEND_URL}/api/admin/overview/users/${userId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(editForm),
      });
      if (res.ok) {
        toast.success("Usuario atualizado!");
        setEditingUser(null);
        fetchUsers();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Erro ao salvar");
      }
    } catch (e) {
      toast.error("Erro ao salvar usuario");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (userId, currentActive) => {
    try {
      await authService.fetchAuth(`${BACKEND_URL}/api/admin/overview/users/${userId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ativo: !currentActive }),
      });
      toast.success(currentActive ? "Usuario desativado" : "Usuario ativado");
      fetchUsers();
    } catch (e) {
      toast.error("Erro ao alterar status");
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm("Tem certeza que deseja excluir este usuario?")) return;
    try {
      const res = await authService.fetchAuth(`${BACKEND_URL}/api/admin/overview/users/${userId}`, { method: "DELETE" });
      if (res.ok) {
        toast.success("Usuario excluido");
        fetchUsers();
      }
    } catch (e) {
      toast.error("Erro ao excluir");
    }
  };

  const addUser = async () => {
    try {
      setSaving(true);
      const res = await authService.fetchAuth(`${BACKEND_URL}/api/admin/overview/users`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...newUser, tipo: activeTab === "afiliados" ? "afiliado" : "fornecedor" }),
      });
      if (res.ok) {
        toast.success("Usuario criado!");
        setShowAddModal(false);
        setNewUser({ nome: "", email: "", senha: "ClickHub2026!", tipo: "afiliado", telefone: "" });
        fetchUsers();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Erro ao criar");
      }
    } catch (e) {
      toast.error("Erro ao criar usuario");
    } finally {
      setSaving(false);
    }
  };

  const filtered = users.filter(u =>
    u.nome?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="p-6 space-y-6" data-testid="admin-overview">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="inline-block mb-2">
            <span className="bg-gradient-to-r from-red-500 to-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">
              Admin - Visao Geral
            </span>
          </div>
          <h1 className="text-3xl font-bold text-white">Gerenciar Usuários</h1>
          <p className="text-sm text-gray-400 mt-1">Visualize e edite dados de Afiliados e Fornecedores</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchUsers} variant="outline" size="sm" className="border-gray-700" data-testid="refresh-overview-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button onClick={() => setShowAddModal(true)} size="sm" className="bg-orange-500 hover:bg-orange-600" data-testid="add-user-btn">
            <Plus className="w-4 h-4 mr-1" /> Novo Usuario
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2">
        <Button
          variant={activeTab === "afiliados" ? "default" : "outline"}
          size="sm"
          onClick={() => setActiveTab("afiliados")}
          className={activeTab === "afiliados" ? "bg-green-600 hover:bg-green-700" : "border-gray-700"}
          data-testid="tab-afiliados"
        >
          <ShoppingBag className="w-4 h-4 mr-2" /> Afiliados ({activeTab === "afiliados" ? filtered.length : ""})
        </Button>
        <Button
          variant={activeTab === "fornecedores" ? "default" : "outline"}
          size="sm"
          onClick={() => setActiveTab("fornecedores")}
          className={activeTab === "fornecedores" ? "bg-blue-600 hover:bg-blue-700" : "border-gray-700"}
          data-testid="tab-fornecedores"
        >
          <Store className="w-4 h-4 mr-2" /> Fornecedores ({activeTab === "fornecedores" ? filtered.length : ""})
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
        <Input
          placeholder="Buscar por nome ou email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9 bg-gray-800/50 border-gray-700"
          data-testid="search-users"
        />
      </div>

      {/* Users List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
        </div>
      ) : filtered.length === 0 ? (
        <Card className="bg-gray-800/30 border-gray-700 p-12 text-center">
          <Users className="w-12 h-12 mx-auto mb-3 text-gray-600" />
          <p className="text-gray-400">Nenhum {activeTab === "afiliados" ? "afiliado" : "fornecedor"} encontrado</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((user) => (
            <Card key={user.id} className="bg-gray-800/40 border-gray-700 overflow-hidden">
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold
                      ${user.tipo === "fornecedor" ? "bg-blue-500/20 text-blue-400" : "bg-green-500/20 text-green-400"}`}>
                      {user.nome?.charAt(0)?.toUpperCase() || "?"}
                    </div>
                    <div>
                      {editingUser === user.id ? (
                        <Input
                          value={editForm.nome}
                          onChange={(e) => setEditForm({ ...editForm, nome: e.target.value })}
                          className="h-7 text-sm bg-gray-700 border-gray-600 w-48"
                        />
                      ) : (
                        <p className="text-sm font-semibold text-white">{user.nome}</p>
                      )}
                      {editingUser === user.id ? (
                        <Input
                          value={editForm.email}
                          onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                          className="h-7 text-xs bg-gray-700 border-gray-600 w-48 mt-1"
                        />
                      ) : (
                        <p className="text-xs text-gray-400 flex items-center gap-1">
                          <Mail className="w-3 h-3" /> {user.email}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase
                      ${user.ativo ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}`}>
                      {user.ativo ? "Ativo" : "Inativo"}
                    </span>
                    {editingUser === user.id ? (
                      <>
                        <Button size="sm" variant="outline" onClick={() => saveEdit(user.id)} disabled={saving} className="h-7 border-green-700 text-green-400 hover:bg-green-900/30">
                          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                        </Button>
                        <Button size="sm" variant="outline" onClick={cancelEdit} className="h-7 border-gray-700">
                          <X className="w-3 h-3" />
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button size="sm" variant="outline" onClick={() => startEdit(user)} className="h-7 border-gray-700" data-testid={`edit-user-${user.id}`}>
                          <Edit2 className="w-3 h-3" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => toggleActive(user.id, user.ativo)} className={`h-7 ${user.ativo ? "border-yellow-700 text-yellow-400" : "border-green-700 text-green-400"}`}>
                          {user.ativo ? <AlertCircle className="w-3 h-3" /> : <Check className="w-3 h-3" />}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setExpandedUser(expandedUser === user.id ? null : user.id)} className="h-7 border-gray-700">
                          {expandedUser === user.id ? <ChevronUp className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => deleteUser(user.id)} className="h-7 border-red-800 text-red-400 hover:bg-red-900/30">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>

                {/* Expanded Details */}
                {expandedUser === user.id && (
                  <div className="mt-3 pt-3 border-t border-gray-700 grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="p-2 bg-gray-900/50 rounded-lg">
                      <p className="text-[10px] text-gray-500 uppercase">ID</p>
                      <p className="text-xs text-gray-300 font-mono truncate">{user.id}</p>
                    </div>
                    <div className="p-2 bg-gray-900/50 rounded-lg">
                      <p className="text-[10px] text-gray-500 uppercase">Telefone</p>
                      <p className="text-xs text-gray-300">{user.telefone || "Nao informado"}</p>
                    </div>
                    <div className="p-2 bg-gray-900/50 rounded-lg">
                      <p className="text-[10px] text-gray-500 uppercase">Tipo</p>
                      <p className="text-xs text-gray-300 capitalize">{user.tipo}</p>
                    </div>
                    <div className="p-2 bg-gray-900/50 rounded-lg">
                      <p className="text-[10px] text-gray-500 uppercase">Criado em</p>
                      <p className="text-xs text-gray-300">{user.created_at ? new Date(user.created_at).toLocaleDateString('pt-BR') : "N/A"}</p>
                    </div>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Add User Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center overflow-y-auto py-8 z-50" onClick={() => setShowAddModal(false)}>
          <Card className="bg-gray-900 border-gray-700 p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-white">Novo {activeTab === "afiliados" ? "Afiliado" : "Fornecedor"}</h2>
              <Button size="sm" variant="ghost" onClick={() => setShowAddModal(false)}><X className="w-4 h-4" /></Button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-400 mb-1 block">Nome</label>
                <Input value={newUser.nome} onChange={(e) => setNewUser({ ...newUser, nome: e.target.value })} className="bg-gray-800 border-gray-700" data-testid="new-user-nome" />
              </div>
              <div>
                <label className="text-xs text-gray-400 mb-1 block">Email</label>
                <Input value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} className="bg-gray-800 border-gray-700" data-testid="new-user-email" />
              </div>
              <div>
                <label className="text-xs text-gray-400 mb-1 block">Senha</label>
                <Input value={newUser.senha} onChange={(e) => setNewUser({ ...newUser, senha: e.target.value })} className="bg-gray-800 border-gray-700" data-testid="new-user-senha" />
              </div>
              <div>
                <label className="text-xs text-gray-400 mb-1 block">Telefone</label>
                <Input value={newUser.telefone} onChange={(e) => setNewUser({ ...newUser, telefone: e.target.value })} className="bg-gray-800 border-gray-700" data-testid="new-user-telefone" />
              </div>
              <Button onClick={addUser} disabled={saving || !newUser.nome || !newUser.email} className="w-full bg-orange-500 hover:bg-orange-600" data-testid="confirm-add-user-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                Criar {activeTab === "afiliados" ? "Afiliado" : "Fornecedor"}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminOverview;
